import API_BASE_URL from '../config';
import { getToken } from './authService';
import { getCategoryPath, getCategoryIdStr } from './campaignService';

let stickersPromise = null;

export async function getStickers(forceRefresh = false) {
  if (forceRefresh) stickersPromise = null;
  if (stickersPromise) return stickersPromise;

  const url = forceRefresh
    ? `${API_BASE_URL}/campaigns/stickers?_t=${Date.now()}`
    : `${API_BASE_URL}/campaigns/stickers`;

  stickersPromise = fetch(url)
    .then(async (res) => {
      if (!res.ok) throw new Error('Failed to fetch stickers');
      const data = await res.json();
      return Array.isArray(data.stickers) ? data.stickers : [];
    })
    .catch((err) => {
      stickersPromise = null;
      console.error('Error fetching stickers:', err);
      return [];
    });

  return stickersPromise;
}

export async function updateStickers(stickers) {
  const token = getToken();
  const res = await fetch(`${API_BASE_URL}/campaigns/stickers`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify({ stickers })
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || 'Dështoi ruajtja e stickers');
  }

  const result = await res.json();
  stickersPromise = null; // Invalidate cache

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('stickersUpdated', {
      detail: { stickers: result.stickers || stickers }
    }));
  }

  return result;
}

export async function uploadStickerImage(file) {
  const token = getToken();
  const formData = new FormData();
  formData.append('stickerImage', file);

  const res = await fetch(`${API_BASE_URL}/campaigns/stickers/upload`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`
    },
    body: formData
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || 'Dështoi ngarkimi i imazhit');
  }

  return await res.json();
}

/**
 * Checks whether a given product matches a sticker rule
 */
export function matchesSticker(product, sticker, categoryTree = []) {
  if (!product || !sticker || !sticker.enabled) return false;

  const productSku = String(product.sku || product.kodi_i_produktit || '').trim().toUpperCase();

  // 1. Check SKU match
  if (Array.isArray(sticker.skus) && sticker.skus.length > 0) {
    const hasSkuMatch = sticker.skus.some(
      s => String(s || '').trim().toUpperCase() === productSku
    );
    if (hasSkuMatch) return true;
  }

  // 2. Check Category match
  if (Array.isArray(sticker.categoryIds) && sticker.categoryIds.length > 0) {
    const targetCatIds = new Set(sticker.categoryIds.map(id => String(id).trim()));

    // Collect product's category identifiers
    const pCatId = getCategoryIdStr(product.categoryId || product.category_id || (product.category && product.category.id));
    const pCategoryIds = Array.isArray(product.categoryIds)
      ? product.categoryIds.map(String)
      : [];

    if (pCatId && targetCatIds.has(pCatId)) return true;
    if (pCategoryIds.some(cid => targetCatIds.has(String(cid)))) return true;

    // Check ancestor categories if includeSubcategories is enabled
    if (sticker.includeSubcategories !== false && pCatId && Array.isArray(categoryTree) && categoryTree.length > 0) {
      const path = getCategoryPath(pCatId, categoryTree);
      if (Array.isArray(path) && path.some(node => targetCatIds.has(getCategoryIdStr(node?.id || node)))) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Returns all active stickers that apply to a specific product
 */
export function getProductStickers(product, stickers = [], categoryTree = []) {
  if (!product || !Array.isArray(stickers) || stickers.length === 0) return [];
  return stickers.filter(s => matchesSticker(product, s, categoryTree));
}
